/**
 *
 * @module userProfileApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/features_overviews/user-profile.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/UserProfileApi.md
 * @desc An account may have multiple user profiles associated with it. The UserProfileApi will be used by
 * applications to control the creation, modification, deletion of user profiles, and setting a user profile as active.
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Account from './../account/entry';
import Token from './../token/entry';

const Util = Services.Util;
const AccountManager = Account.AccountManager;
const TokenManager = Token.TokenManager;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const UserProfile = Services.Account.UserProfile;
const UserProfileApiDustUrnReference = Services.Internal.Dust.DustUrnReference.userProfile.userProfileApi;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class UserProfileApi
 * @since 3.5.0
 * @desc Provides the ability to manipulate user profiles.
 *
 */
export default class UserProfileApi {

    /**
     *
     * @param {SDK.Account.AccountManager} accountManager
     * @param {SDK.Token.TokenManager} tokenManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(accountManager, tokenManager, logger = Logging) {

        const errorMessage = {
            ctor: 'UserProfileApi(accountManager, tokenManager, logger)',
            get accountManager() {
                return `${this.ctor} needs AccountManager instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs TokenManager instance`;
            }
        };

        instanceStrict(accountManager, AccountManager, errorMessage.accountManager, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Account.AccountManager}
         *
         */
        this.accountManager = accountManager;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.accountManager.client.dustEnabled);

        this.logger.info(UserProfileApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {String} profileName - The name of the profile.
     * @param {Object} [attributes=null] - The attributes to use when creating the profile.
     * @param {Object} [metadata=null] - The metadata to use when creating the profile.
     * @desc Creates a user profile.
     * @throws {AccountNotFoundException} The account was not found
     * @note The account could not be found or the operation failed. Account service returns 401 or 404 with
     * error: account.get.failed, account.not.found.
     * @throws {MaximumProfilesReachedException} The maximum number of profiles for the user's account has already
     * been reached.
     * @note Request to create a profile returns HTTP 400 with code of account.profiles.max.exceeded
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Account.UserProfile>} A promise that completes when the operation has succeeded.
     *
     */
    createUserProfile(profileName, attributes = null, metadata = null) {

        const errorMessage = {
            method: 'AccountApi.createUserProfile(profileName, attributes, metadata)',
            get profileName() {
                return `${this.method} profileName should be a String with length`;
            },
            get attributes() {
                return `${this.method} attributes should be a non empty Object`;
            },
            get metadata() {
                return `${this.method} metadata should be a non empty Object`;
            }
        };

        nonEmptyString(profileName, errorMessage.profileName, InvalidArgumentException);

        if (Check.assigned(attributes)) {
            nonEmptyObject(attributes, errorMessage.attributes, InvalidArgumentException);
        }

        if (Check.assigned(metadata)) {
            nonEmptyObject(metadata, errorMessage.metadata, InvalidArgumentException);
        }

        const { dustEnabled, logger, accountManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: UserProfileApi.name,
            urn: UserProfileApiDustUrnReference.createUserProfile
        });

        return accountManager.createUserProfile(profileName, attributes, metadata).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Account.UserProfile} profile - The user profile to delete.
     * @desc Deletes the user profile.
     * @note if services returns an account grant it will be exchanged for a access token.
     * @throws {UserProfileNotFoundException} The userProfile was not found
     * @note The userProfile could not be found. UserProfile service returns
     * 400, 401 or 404 with error: account.profile.not.found.
     * @throws {UserProfileDeleteFailedException} The userProfile failed to be deleted.
     * @note The userProfile could not be deleted. UserProfile service returns a 400 with
     * error: account.profile.delete.failed.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} A promise that completes when the operation has succeeded.
     *
     */
    deleteUserProfile(profile) {

        const errorMessage = {
            method: 'AccountApi.deleteUserProfile(profile)',
            get profile() {
                return `${this.method} profile should be an instance of UserProfile`;
            }
        };

        instanceStrict(profile, UserProfile, errorMessage.profile, InvalidArgumentException);

        const { dustEnabled, logger, accountManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: UserProfileApi.name,
            urn: UserProfileApiDustUrnReference.deleteUserProfile
        });

        return accountManager.deleteUserProfile(profile).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @desc Returns the user profile currently active for the account.
     * @throws {AccountNotFoundException} The account was not found
     * @note The account could not be found or the operation failed. Account service returns 401 or 404 with
     * error: account.get.failed, account.not.found.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Account.UserProfile>} A promise that completes when
     * the operation has succeeded and returns the user profile.
     *
     */
    getActiveUserProfile() {

        const { dustEnabled, logger, accountManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: UserProfileApi.name,
            urn: UserProfileApiDustUrnReference.getActiveUserProfile
        });

        return accountManager.getActiveUserProfile().catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} profileId - The ID of the user profile to activate.
     * @desc Returns the user profile specified by the profileId.
     * @note Retrieves the Account, and returns the `activeProfile` property from it.
     * @throws {AccountNotFoundException} The account was not found
     * @note The account could not be found or the operation failed. Account service returns 401 or 404 with
     * error: account.get.failed, account.not.found.
     * @throws {UserProfileNotFoundException} The userProfile was not found
     * @note The userProfile could not be found. UserProfile service returns
     * 400, 401 or 404 with error: account.profile.not.found.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Account.UserProfile>} A promise that completes when
     * the operation has succeeded and returns the user profile.
     *
     */
    getUserProfile(profileId) {

        const errorMessage = {
            method: 'AccountApi.getUserProfile(profileId)',
            get profileId() {
                return `${this.method} profileId should be a String with length`;
            }
        };

        nonEmptyString(profileId, errorMessage.profileId, InvalidArgumentException);

        const { dustEnabled, logger, accountManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: UserProfileApi.name,
            urn: UserProfileApiDustUrnReference.getUserProfile
        });

        return accountManager.getUserProfile(profileId).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @desc Returns the user profiles.
     * @throws {AccountNotFoundException} The account was not found
     * @note The account could not be found or the operation failed. Account service returns 401 or 404 with
     * error: account.get.failed, account.not.found.
     * @throws {ProfileRetrievalException} The profiles for the user's account could not be retrieved.
     * @note Profiles for the user's account could not be read. UserProfile service returns
     * 404 with error: account.profile.read.failed.
     * @throws {MaximumProfilesReachedException} The maximum number of profiles for the user's account has already
     * been reached.
     * @note Request to create a profile returns HTTP 400 with code of account.profiles.max.exceeded
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Array<SDK.Services.Account.UserProfile>>} A promise that completes when
     * the operation has succeeded and returns all user profiles.
     *
     */
    getUserProfiles() {

        const { dustEnabled, logger, accountManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: UserProfileApi.name,
            urn: UserProfileApiDustUrnReference.getUserProfiles
        });

        return accountManager.getUserProfiles().catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Account.UserProfile} profile - The user profile to activate.
     * @desc Sets the active user profile.
     * @note Activates the user profile, and exchanges the account grant
     * returned by AccountClient.setActiveUserProfile() for a new AccessContext.
     * @throws {AccountNotFoundException} The account was not found
     * @note The account could not be found or the operation failed. Account service returns 401 or 404 with
     * error: account.get.failed, account.not.found.
     * @throws {UserProfileNotFoundException} The userProfile was not found
     * @note The userProfile could not be found. UserProfile service returns
     * 400, 401 or 404 with error: account.profile.not.found.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Void>} A promise that completes when the operation has succeeded.
     *
     */
    setActiveUserProfile(profile) {

        const errorMessage = {
            method: 'AccountApi.setActiveUserProfile(profile)',
            get profile() {
                return `${this.method} profile should be an instance of UserProfile`;
            }
        };

        instanceStrict(profile, UserProfile, errorMessage.profile, InvalidArgumentException);

        const { dustEnabled, logger, accountManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: UserProfileApi.name,
            urn: UserProfileApiDustUrnReference.setActiveUserProfile
        });

        return accountManager.setActiveUserProfile(profile).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Account.UserProfile} profile - The user profile to update.
     * @desc Saves all changes made to the user profile.
     * @note The service may change the profile name or attributes, so the SDK
     * should ensure that the updated user profile is returned.
     * @throws {AccountNotFoundException} The account was not found
     * @note The account could not be found or the operation failed. Account service returns 401 or 404 with
     * error: account.get.failed, account.not.found.
     * @throws {UserProfileNotFoundException} The userProfile was not found
     * @note The userProfile could not be found. UserProfile service returns
     * 400, 401 or 404 with error: account.profile.not.found.
     * @throws {UserProfileUpdateFailedException} The userProfile update failed
     * @note The userProfile update failed. UserProfile service returns
     * 400, 401 or 404 with error: account.profile.update.failed.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<SDK.Services.Account.UserProfile>} A promise that completes when the operation has succeeded.
     *
     */
    updateUserProfile(profile) {

        const errorMessage = {
            method: 'AccountApi.updateUserProfile(profile)',
            get profile() {
                return `${this.method} profile should be an instance of UserProfile`;
            }
        };

        instanceStrict(profile, UserProfile, errorMessage.profile, InvalidArgumentException);

        const { dustEnabled, logger, accountManager } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: UserProfileApi.name,
            urn: UserProfileApiDustUrnReference.updateUserProfile
        });

        return accountManager.updateUserProfile(profile).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

}



// WEBPACK FOOTER //
// ./src/userProfile/userProfileApi.js